import { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";

const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";
const emptyForm = { name: "", book: "", quote: "", order: 0 };

export const TestimonialsEditor = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, order: parseInt(form.order) || 0 };
      if (editing) {
        await api.put(`/admin/testimonials/${editing}`, payload);
        toast.success("Testimonial updated");
      } else {
        await api.post("/admin/testimonials", payload);
        toast.success("Testimonial added");
      }
      setForm(emptyForm);
      setEditing(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setBusy(false); }
  };

  const edit = (t) => { setEditing(t.id); setForm({ name: t.name, book: t.book || "", quote: t.quote, order: t.order || 0 }); };

  const remove = async (t) => {
    if (!window.confirm(`Delete testimonial from ${t.name}?`)) return;
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      toast.success("Testimonial deleted");
      if (editing === t.id) { setEditing(null); setForm(emptyForm); }
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="testimonials-admin-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading">Testimonials</h1>
        <p className="text-neutral-500 mt-1">Author quotes shown on the landing and about pages.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={save} className="bg-white border border-neutral-200 rounded-lg p-6 h-fit" data-testid="testimonial-form">
          <h3 className="font-semibold font-heading mb-4 flex items-center justify-between">
            {editing ? "Edit testimonial" : "Add testimonial"}
            {editing && (
              <button type="button" data-testid="testimonial-cancel-edit" onClick={() => { setEditing(null); setForm(emptyForm); }}
                className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"><X size={12} /> Cancel</button>
            )}
          </h3>
          <label className="text-sm font-medium text-neutral-700">Author name</label>
          <input data-testid="testimonial-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
          <label className="text-sm font-medium text-neutral-700 mt-3 block">Attribution (e.g. "Author of ...")</label>
          <input data-testid="testimonial-book-input" value={form.book} onChange={set("book")} className={inputCls} />
          <label className="text-sm font-medium text-neutral-700 mt-3 block">Quote</label>
          <textarea data-testid="testimonial-quote-input" required rows={4} value={form.quote} onChange={set("quote")} className={inputCls} />
          <label className="text-sm font-medium text-neutral-700 mt-3 block">Display order</label>
          <input data-testid="testimonial-order-input" type="number" value={form.order} onChange={set("order")} className={inputCls} />
          <button data-testid="testimonial-save-button" disabled={busy}
            className="mt-4 w-full py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <Plus size={15} /> {editing ? "Save changes" : "Add testimonial"}
          </button>
        </form>

        <div className="lg:col-span-2 space-y-4">
          {items.length === 0 && <p className="text-neutral-400 text-sm">No testimonials yet.</p>}
          {items.map((t) => (
            <div key={t.id} data-testid={`testimonial-row-${t.id}`} className="bg-white border border-neutral-200 rounded-lg p-6 flex gap-4">
              <div className="flex-1">
                <p className="text-sm text-neutral-800 leading-relaxed">“{t.quote}”</p>
                <p className="font-semibold text-neutral-900 font-heading mt-3">{t.name}</p>
                <p className="text-xs text-neutral-400">{t.book} · order {t.order || 0}</p>
              </div>
              <div className="flex flex-col gap-2">
                <button data-testid={`testimonial-edit-${t.id}`} onClick={() => edit(t)}
                  className="p-2 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 rounded-md"><Pencil size={15} /></button>
                <button data-testid={`testimonial-delete-${t.id}`} onClick={() => remove(t)}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
